import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

const ETI = 'assets/img/etiquetas/';
const ICO = 'assets/img/iconos-denominaciones/';

@Component({
  selector: 'app-etiqueta',
  templateUrl: './etiqueta.component.html',
  styleUrls: ['./etiqueta.component.scss'],
})
export class EtiquetaComponent implements OnInit, AfterViewInit, OnDestroy {
  typed = '';
  private typeTimer?: ReturnType<typeof setTimeout>;

  // Etiquetas (sin texto / con textos) — variedades reales
  private readonly variants = ['Earlyna', 'Leanri', 'M7', 'Nadorcott', 'Sando', 'SummerPrim'];
  readonly labels = this.variants.map((v) => ETI + v + '_Etiqueta_Final_Troquel.webp');
  readonly labelsText = this.variants.map((v) => ETI + v + '_Etiqueta_Final_Troquel_Textos.webp');

  labelIndex = 0;
  textIndex = 0;
  labelClass = 'scale-in';
  textClass = 'scale-in';
  centerTransform = 'perspective(500px) scale(1)';
  private rotTimer1?: ReturnType<typeof setInterval>;
  private rotTimer2?: ReturnType<typeof setInterval>;

  // Tarjetas de tipos de producto (textos en i18n etiqueta.cards)
  readonly cardIcons = [ICO + 'productos_06.webp', ICO + 'productos_03.webp', ICO + 'productos_04.webp', ICO + 'productos_05.webp'];
  readonly cardIndexes = [0, 1, 2, 3];
  readonly qualityIndexes = [0, 1, 2, 3];

  readonly apiImg = 'assets/img/sistema/API.svg';

  @ViewChild('cards') private cards?: ElementRef<HTMLElement>;
  private io?: IntersectionObserver;
  private langSub?: Subscription;

  constructor(private readonly translate: TranslateService) {}

  ngOnInit(): void {
    this.langSub = this.translate.onLangChange.subscribe(() => this.startTypewriter());
  }

  ngAfterViewInit(): void {
    this.translate.get('etiqueta.title').subscribe(() => this.startTypewriter());

    const items = this.cards?.nativeElement.querySelectorAll('.type-card');
    if (items) {
      this.io = new IntersectionObserver(
        (entries) => entries.forEach((e) => e.target.classList.toggle('in-view', e.isIntersecting)),
        { threshold: 0.25 },
      );
      items.forEach((c) => this.io!.observe(c));
    }

    // Auto-rotación de ambas etiquetas cada 5s (fundido de opacidad)
    setTimeout(() => {
      this.rotTimer1 = setInterval(() => this.rotate('label'), 5000);
      this.rotTimer2 = setInterval(() => this.rotate('text'), 5000);
    }, 100);
  }

  private startTypewriter(): void {
    if (this.typeTimer) clearTimeout(this.typeTimer);
    const full = this.translate.instant('etiqueta.title') as string;
    this.typed = '';
    let i = 0;
    const step = () => {
      if (i < full.length) {
        this.typed += full.charAt(i++);
        this.typeTimer = setTimeout(step, 40);
      }
    };
    step();
  }

  // Transición de fundido (opacity 1→0→1) al cambiar de etiqueta
  private rotate(which: 'label' | 'text'): void {
    if (which === 'label') {
      this.labelClass = 'scale-out';
      setTimeout(() => {
        this.labelIndex = (this.labelIndex + 1) % this.labels.length;
        this.labelClass = 'scale-in';
      }, 700);
    } else {
      this.textClass = 'scale-out';
      setTimeout(() => {
        this.textIndex = (this.textIndex + 1) % this.labelsText.length;
        this.textClass = 'scale-in';
      }, 700);
    }
  }

  // Efecto 3D de perspectiva sobre la etiqueta central
  onLabelMove(e: MouseEvent): void {
    const el = e.target as HTMLImageElement;
    const rect = el.getBoundingClientRect();
    const posX = e.clientX - rect.left;
    const posY = e.clientY - rect.top;
    const rotY = 40 * ((posX - rect.width / 2) / rect.width);
    const rotX = -40 * ((posY - rect.height / 2) / rect.height);
    this.centerTransform = `perspective(500px) scale(1.1) rotateX(${rotX}deg) rotateY(${rotY}deg)`;
  }
  onLabelOut(): void {
    this.centerTransform = 'perspective(500px) scale(1) rotateX(0) rotateY(0)';
  }
  onLabelDown(): void {
    this.centerTransform = 'perspective(500px) scale(0.9) rotateX(0) rotateY(0)';
  }
  onLabelUp(): void {
    this.centerTransform = 'perspective(500px) scale(1.1) rotateX(0) rotateY(0)';
  }

  ngOnDestroy(): void {
    if (this.typeTimer) clearTimeout(this.typeTimer);
    if (this.rotTimer1) clearInterval(this.rotTimer1);
    if (this.rotTimer2) clearInterval(this.rotTimer2);
    this.io?.disconnect();
    this.langSub?.unsubscribe();
  }
}
